/**
 * brand-health: how a chart point of each widget maps back to the rows behind it,
 * for the conversations drill-down. Same filtering as the widget's loader.
 *
 * The prototype drilled through `pointFrom(…, result, …)`, i.e. the result's
 * `rowsAt(index)`: for a time series every row in the bucket **across every
 * series**, for a pivot every row in the category.
 */
import type { DashboardQuery, DrillRef } from '@/data-sources/contract';
import { bucketStart } from '@/lib/analytics/buckets';
import { isoToMs } from '@/lib/analytics/dates';

import type { MentionRow } from '../engine/generator';
import { scoped } from '../query';
import type { DrillResolver, DrillResolvers } from './types';

/** `aggregate(…).rowsAt(index)`: the user's brand rows in the clicked bucket. */
const byBucket: DrillResolver = (ref: DrillRef, query: DashboardQuery) => {
  const rows = scoped(query).own;
  const bucket = ref.bucket;
  if (!bucket) return rows;
  const start = isoToMs(bucket.start);
  return rows.filter((row) => bucketStart(row.day, bucket.unit) === start);
};

/** `pivot(…).rowsAt(index)`: the user's brand rows in the clicked category. */
const byCategory =
  (field: 'origin' | 'topic'): DrillResolver =>
  (ref, query) => {
    const rows = scoped(query).own;
    const category = ref.category;
    if (!category) return rows;
    return rows.filter((row: MentionRow) => row[field] === category.value);
  };

export const brandHealthDrillResolvers: DrillResolvers = {
  nps: byBucket,
  'seeded-vs-organic': byCategory('origin'),
  'sentiment-over-time': byBucket,
  'engagement-volume': byBucket,
  'engagement-growth': byBucket,
  'topic-sentiment-split': byCategory('topic'),
};
